import React, { useContext, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useIntl } from 'react-intl'
import dayjs from 'dayjs'
import { EOrgType, EPlatformType } from '@unfoldrtech/portal-mic'
import {
  EParamChannelTypeGlobal,
  IDAReportProductsTable,
  EReportColumnHeader,
  EReportingTimeWindow,
} from '@unfoldrtech/portal-mic/dist/src/models/core'

import { ReportPageTemplateLayout } from '../../components/ReportPageTemplateLayout'
import { ReportingProductsChartWrapper } from './ReportingProductsChartWrapper'

import {
  ISPReportProductsTable,
  TReportExportType,
  TReportingFilters,
} from '../../../../models'
import {
  AdGroupContext,
  AppContext,
  CampaignContext,
} from '../../../../models/contexts'

import {
  selectProductsTableReportingFilters,
  selectReportingFilters,
  setAdGroupIds,
  setCampaignIds,
  setIsReportingV3,
} from '../../../../store/reportingFilters'

import {
  NOT_AVAILABLE,
  exportFileDateFormat,
} from '../../../../utils/constants'
import { EXPORT_PAGE_LIMIT, exportTableDataToFile } from '../../helpers'
import { useGetDatasetKeyTableWhitelist } from '../../hooks/use-get-dataset-key-table-whitelist'
import { ReportingProductsTableWrapper } from './ReportingProductsTableWrapper'
import {
  convertChannelTypeToParam,
  convertEParamChannelTypeToEParamChannelTypeGlobal,
  generateCorrelationId,
  getTimezone,
} from '../../../../utils/helpers'
import { sanitizeDataTestId } from '../../../../utils'
import { TDatasetWhitelistKey } from '../../models'
import { getProductEDatasetKeyFromChannelTypeGlobal } from '../helpers/get-product-e-dataset-key-from-channel-type-global'
import { coreAPI } from '../../../../services/coreAPI'
import { useGetRetailerAndAdvertiserIds } from '../../hooks/use-get-retailer-and-advertiser-ids'
import { ErrorToast } from '../../../ToastCard/ErrorToast'
import { SuccessToast } from '../../../ToastCard/SuccessToast'

/**
 * One report template per entity
 * Ex: ReportCategoriesTemplate, ReportCampaignsTemplate, etc ...
 */
export const ReportingProductsTemplate = ({
  channelType: propChannelType,
}: {
  channelType?: EParamChannelTypeGlobal
}) => {
  const pageTitle = propChannelType
    ? `reporting.header.products.${sanitizeDataTestId(propChannelType)}`
    : 'reporting.header.products'

  const dispatch = useDispatch()
  const intl = useIntl()

  const [appContext] = useContext(AppContext)
  const campaign = useContext(CampaignContext)
  const adGroup = useContext(AdGroupContext)
  const { channelType: reportingFilterChannelType } =
    useSelector(selectReportingFilters)
  const filters = useSelector(selectProductsTableReportingFilters)

  const channelType =
    propChannelType ||
    convertEParamChannelTypeToEParamChannelTypeGlobal(
      reportingFilterChannelType ||
        convertChannelTypeToParam(campaign.channelType)
    )

  const { retailerId, advertiserId } = useGetRetailerAndAdvertiserIds()
  const orgType = appContext.userOrg!.type

  const [isExporting, setIsExporting] = useState(false)
  const [showExportErrorToast, setShowExportErrorToast] = useState(false)
  const [showExportCappedToast, setShowExportCappedToast] = useState(false)

  const [tableData, setTableData] = useState<
    ISPReportProductsTable | IDAReportProductsTable
  >()

  const [reportingFiltersIsSetup, setReportingFiltersIsSetup] = useState(false)

  const onTableDataChange = (
    productsTableData: ISPReportProductsTable | IDAReportProductsTable
  ) => {
    setTableData(productsTableData)
  }

  const whitelistData = useGetDatasetKeyTableWhitelist({
    key:
      (tableData?.datasetKey as TDatasetWhitelistKey) ||
      getProductEDatasetKeyFromChannelTypeGlobal(channelType),
  })

  const onExportData = async (
    { startDate: start, endDate: end }: TReportingFilters,
    type: TReportExportType
  ) => {
    const orgName =
      appContext.userOrg?.name ?? appContext.userOrg?.type ?? NOT_AVAILABLE
    const translatedPageTitle = intl
      .formatMessage({ id: pageTitle })
      .replace(/[-]+/g, '')
      .replace(/[ ]+/g, '_')
    const sDate = dayjs(start).format(exportFileDateFormat)
    const eDate = dayjs(end).format(exportFileDateFormat)

    const fileName = `${orgName}_${translatedPageTitle}_${sDate}_${eDate}`
    setIsExporting(true)
    setShowExportErrorToast(false)
    setShowExportCappedToast(false)
    try {
      const res =
        orgType === EOrgType.Advertiser
          ? await coreAPI.v2AdvertiserReporting().getAdvertiserProductsReportTable(
              advertiserId,
              generateCorrelationId(),
              retailerId,
              campaign?.id,
              adGroup?.id,
              channelType as unknown as EParamChannelTypeGlobal,
              filters.productId,
              filters.platformType as unknown as EPlatformType,
              filters.sortBy as EReportColumnHeader,
              filters.sortOrder,
              filters.timeWindow as unknown as EReportingTimeWindow,
              start,
              end,
              filters.tzZone || getTimezone(),
              EXPORT_PAGE_LIMIT,
              0,
              filters.filterBy,
              filters.filterOperation,
              filters.filterValue,
              filters.name ? encodeURI(filters.name) : undefined,
              // @ts-expect-error: fix in PORT-2535
              filters.status
            )
          : await coreAPI.v2RetailerReporting().getRetailerProductsReportTable(
              retailerId,
              generateCorrelationId(),
              advertiserId,
              campaign?.id,
              adGroup?.id,
              channelType as unknown as EParamChannelTypeGlobal,
              filters.productId,
              filters.platformType as unknown as EPlatformType,
              filters.sortBy as EReportColumnHeader,
              filters.sortOrder,
              filters.timeWindow as EReportingTimeWindow,
              start,
              end,
              filters.tzZone || getTimezone(),
              EXPORT_PAGE_LIMIT,
              0,
              filters.filterBy,
              filters.filterOperation,
              filters.filterValue,
              filters.name ? encodeURI(filters.name) : undefined,
              // @ts-expect-error: fix in PORT-2535
              filters.status
            )

      const rows = res.data.table?.rows ?? []

      exportTableDataToFile({
        tableDataRows: rows,
        tableHeaders: whitelistData,
        fileName,
        fileType: type,
      })

      if (rows.length >= EXPORT_PAGE_LIMIT) {
        setShowExportCappedToast(true)
      }
    } catch {
      setShowExportErrorToast(true)
    } finally {
      setIsExporting(false)
    }
  }

  useEffect(() => {
    dispatch(
      setCampaignIds({
        campaignIds: campaign?.id,
      })
    )
    dispatch(
      setAdGroupIds({
        adGroupIds: adGroup?.id,
      })
    )
    setReportingFiltersIsSetup(true)
  }, [campaign.id, adGroup.id])

  useEffect(() => {
    dispatch(setIsReportingV3({ IsReportingV3: true }))
  }, [])

  return (
    <>
      {reportingFiltersIsSetup &&
        (reportingFilterChannelType || channelType) && (
          <>
            <ReportPageTemplateLayout
              title={pageTitle}
              isExportDisabled={isExporting || !tableData?.datasetKey}
              chartWrapper={
                <ReportingProductsChartWrapper channelType={channelType} />
              }
              tableWrapper={
                <ReportingProductsTableWrapper
                  onTableDataChange={onTableDataChange}
                  channelType={channelType}
                />
              }
              onExportData={onExportData}
            />
            <SuccessToast
              show={showExportCappedToast}
              onClose={() => setShowExportCappedToast(false)}
              translationKey="reporting.export.capped"
            />
            <ErrorToast
              show={showExportErrorToast}
              onClose={() => setShowExportErrorToast(false)}
              translationKey="reporting.export.failed"
            />
          </>
        )}
    </>
  )
}
